import React, { useState, useCallback, useEffect, useRef } from 'react'

import { NotificationsPanel } from '@platform/alerts'
import { LanguageSelector } from '@platform/i18n/components/LanguageSelector'
import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { useAuth } from '@/platform/auth/hooks/useAuth'
import { useSignupMode } from '@/platform/auth/hooks/useSignupMode'
import { WorkspaceSwitcher } from '@/platform/workspaces/components/WorkspaceSwitcher'
import { AUTHENTICATED_MAIN_NAV_ITEMS } from '@/shared/components/layout/mainNavItems'
import { usePrefetch } from '@/shared/hooks/usePrefetch'
import { useNavbarSticky, addStickyScrollClasses } from '@/shared/hooks/useSticky'
import { useTheme as useThemeContext } from '@/shared/theme'
import { logger } from '@/shared/utils/logger'

interface NavbarProps {
  currentPage: string
  onNavigate: (page: string) => void
  onOpenAuthModal: (mode: 'login' | 'register') => void
}

/** What `usePrefetch` returns — hover/focus handlers for a lazy route chunk. */
interface PrefetchHandlers {
  onMouseEnter: () => void
  onFocus: () => void
}

export const Navbar = React.memo<NavbarProps>(
  ({ currentPage, onNavigate, onOpenAuthModal }: NavbarProps) => {
    const [userMenuOpen, setUserMenuOpen] = useState(false)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const navbarRef = useRef<HTMLElement>(null)
    const { resolvedTheme, toggleTheme } = useThemeContext()
    const { isScrolled, isVisible } = useNavbarSticky()
    const { t } = useTranslation('shared')
    // config auth/signups: while closed, the sign-up button opens the waitlist
    const { mode: signupMode } = useSignupMode()
    const signUpLabel = signupMode === 'open' ? t('auth.signUp') : t('waitlist.cta', { ns: 'auth' })
    const { isAuthenticated, user, getUserName, getUserInitials, logout } = useAuth()

    const toggleUserMenu = useCallback(() => {
      setUserMenuOpen(!userMenuOpen)
    }, [userMenuOpen])

    const toggleMobileMenu = useCallback(() => {
      setMobileMenuOpen(!mobileMenuOpen)
    }, [mobileMenuOpen])

    // Close menu when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          const userButton = (event.target as Element)?.closest('[data-user-button]')
          if (!userButton) {
            setUserMenuOpen(false)
          }
        }
      }

      document.addEventListener('click', handleClickOutside)
      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }, [])

    // Close user menu when Escape key is pressed
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && userMenuOpen) {
          setUserMenuOpen(false)
        }
      }

      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }, [userMenuOpen])

    // Apply sticky scroll effects
    useEffect(() => {
      if (navbarRef.current) {
        addStickyScrollClasses(navbarRef.current, isScrolled)
      }
    }, [isScrolled])

    const handleLinkClick = (page: string) => {
      onNavigate(page)
    }

    // Prefetch lazy route chunks on hover/focus for faster perceived navigation
    const prefetchTransactions = usePrefetch(
      useCallback(
        () => import('@/modules/accounting/features/transactions/components/TransactionsPage'),
        [],
      ),
    )
    const prefetchImport = usePrefetch(
      useCallback(() => import('@/modules/accounting/features/import'), []),
    )
    const prefetchBalances = usePrefetch(
      useCallback(() => import('@/modules/accounting/features/balances'), []),
    )
    const prefetchMatching = usePrefetch(
      useCallback(() => import('@/modules/accounting/features/transaction-matching'), []),
    )
    const prefetchTaxReport = usePrefetch(
      useCallback(() => import('@/modules/accounting/features/tax-reports/TaxReportPage'), []),
    )
    const prefetchSupport = usePrefetch(
      useCallback(() => import('@/modules/user/features/support/pages/MyTicketsPage'), []),
    )
    // Prefetch handlers keyed by nav slug. The hooks above must stay
    // unconditional (rules of hooks); this map is only the lookup the rendered
    // list uses, so adding a page to AUTHENTICATED_MAIN_NAV_ITEMS without a
    // prefetch entry degrades to "loads on click" instead of breaking.
    const prefetchHandlersBySlug: Record<string, PrefetchHandlers> = {
      transacciones: prefetchTransactions,
      importar: prefetchImport,
      saldos: prefetchBalances,
      matching: prefetchMatching,
      'informe-fiscal': prefetchTaxReport,
    }

    const handleLogout = useCallback(async () => {
      try {
        await logout()
        setUserMenuOpen(false)
        onNavigate('login') // Redirect to login after logout
      } catch (error) {
        logger.error('Logout error:', error)
      }
    }, [logout, onNavigate])

    return (
      <nav
        data-testid="navbar-container"
        ref={navbarRef}
        role="navigation"
        aria-label={t('navigation.mainNav')}
        // Below `sm` the side padding, the row gap and the actions spacing are
        // halved and the logo collapses to its icon: with the desktop sizes the
        // row measured 506 px on a 400 px phone and pushed the user menu (the
        // only way to profile, settings and log out) off-screen.
        className={`bg-crypto-card border-b border-crypto-border px-4 sm:px-6 py-4 fixed top-0 left-0 right-0 z-30 ${isScrolled ? 'sticky-scrolled' : ''} ${isVisible ? '' : 'navbar-hidden'}`}
        style={{ transform: isVisible ? 'translateY(0)' : 'translateY(-100%)' }}
      >
        {/* Layout: 3 in-flow flex regions. Logo and actions use `grow basis-0` so the
            center links stay visually centered while space allows; the links list is
            the only region allowed to shrink (min-w-0 + truncate), so it can NEVER
            underlap the clickable actions block (previous bug: absolute-centered ul). */}
        <div className="flex items-center justify-between gap-2 sm:gap-4 w-full">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 grow basis-0">
            {/* Icon-only mark for phones: the 180 px wordmark alone took half of a
                360 px screen. Decorative duplicate of the full logo below. */}
            <svg
              data-testid="navbar-logo-compact"
              className="h-8 w-8 sm:hidden"
              viewBox="-3 1 84 84"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <defs>
                <clipPath id="nav-interlock-compact">
                  <rect x="24" y="0" width="46" height="28" />
                </clipPath>
              </defs>
              <g transform="translate(0, 6)">
                <rect
                  x="2"
                  y="2"
                  width="52"
                  height="52"
                  rx="10"
                  ry="10"
                  fill="none"
                  stroke="#5E8EA6"
                  strokeWidth="9.5"
                />
                <rect
                  x="24"
                  y="24"
                  width="52"
                  height="52"
                  rx="10"
                  ry="10"
                  fill="none"
                  stroke="#4CB5D7"
                  strokeWidth="9.5"
                />
                <g clipPath="url(#nav-interlock-compact)">
                  <rect
                    x="2"
                    y="2"
                    width="52"
                    height="52"
                    rx="10"
                    ry="10"
                    fill="none"
                    stroke="#5E8EA6"
                    strokeWidth="9.5"
                  />
                </g>
              </g>
            </svg>
            <svg
              data-testid="navbar-logo"
              className="h-9 hidden sm:block"
              style={{ width: '180px' }}
              viewBox="0 0 560 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Icon: two interlocking rounded squares */}
              <defs>
                <clipPath id="nav-interlock">
                  <rect x="24" y="0" width="46" height="28" />
                </clipPath>
              </defs>
              <g transform="translate(0, 6)">
                <rect
                  x="2"
                  y="2"
                  width="52"
                  height="52"
                  rx="10"
                  ry="10"
                  fill="none"
                  stroke="#5E8EA6"
                  strokeWidth="9.5"
                />
                <rect
                  x="24"
                  y="24"
                  width="52"
                  height="52"
                  rx="10"
                  ry="10"
                  fill="none"
                  stroke="#4CB5D7"
                  strokeWidth="9.5"
                />
                <g clipPath="url(#nav-interlock)">
                  <rect
                    x="2"
                    y="2"
                    width="52"
                    height="52"
                    rx="10"
                    ry="10"
                    fill="none"
                    stroke="#5E8EA6"
                    strokeWidth="9.5"
                  />
                </g>
              </g>
              {/* BLOCK text */}
              <text
                x="96"
                y="42"
                fontFamily="Poppins, sans-serif"
                fontWeight="700"
                fontSize="44"
                fill="#CDD1D5"
                letterSpacing="2.5"
              >
                BLOCK
              </text>
              {/* and TAXES text */}
              <text y="82" fontFamily="Poppins, sans-serif">
                <tspan x="96" fontWeight="300" fontSize="29" fill="#858B93" letterSpacing="0.8">
                  {'and '}
                </tspan>
                <tspan fontWeight="700" fontSize="38" fill="#CDD1D5" letterSpacing="2">
                  TAXES
                </tspan>
              </text>
            </svg>
          </div>

          {/* Mobile menu button. Authenticated nav has 5 links + actions and only fits
              in-flow from xl (1280px) up, so it collapses to the hamburger below xl.
              Unauthenticated navbars have no center links, so md keeps its old behavior. */}
          <div className={isAuthenticated ? 'xl:hidden' : 'md:hidden'}>
            <button
              data-testid="navbar-mobile-menu-toggle"
              onClick={toggleMobileMenu}
              // Es un icono suelto: sin texto, sin `title` y sin `aria-label` su
              // nombre accesible quedaba VACÍO, y es el único acceso a la
              // navegación por debajo de xl. Al ser un conmutador tiene que
              // decir además si está abierto y qué controla.
              aria-label={mobileMenuOpen ? t('navbar.closeMobileMenu') : t('navbar.openMobileMenu')}
              aria-expanded={mobileMenuOpen}
              aria-controls="navbar-mobile-menu"
              className="text-crypto-muted hover:text-white p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>

          {/* Navigation Links - centered in-flow between logo and actions.
              min-w-0 lets the list shrink so links truncate with ellipsis instead of
              overlapping the language/notifications/user block on narrow viewports. */}
          {isAuthenticated && (
            <ul
              data-testid="navbar-links"
              className="hidden xl:flex items-center gap-x-2 2xl:gap-x-6 min-w-0 list-none p-0 m-0"
            >
              {user?.role === 'org_admin' ? (
                // Admin navigation — the single way into the org admin panel.
                // It used to point at /admin/templates, which is a super-admin
                // route the org-admin cannot have, so the entry led nowhere.
                <li className="min-w-0">
                  <a
                    data-testid="nav-admin-org"
                    href="/admin/org/dashboard"
                    onClick={(e) => {
                      e.preventDefault()
                      handleLinkClick('admin-org')
                    }}
                    aria-label={t('navbar.manageOrganization')}
                    className={`navbar-link block max-w-full truncate px-3 py-2 ${currentPage === 'admin-org' ? 'text-white border-b-2 border-crypto-accent font-medium' : 'text-crypto-muted hover:text-white font-medium'} transition-all`}
                  >
                    {t('navbar.organizationPanel')}
                  </a>
                </li>
              ) : (
                // User normal navigation — one entry per AUTHENTICATED_MAIN_NAV_ITEMS
                // so a page can never be routed but unreachable (that is how
                // /lots and /tax-loss-harvesting stayed hidden).
                <>
                  {AUTHENTICATED_MAIN_NAV_ITEMS.map((item) => {
                    const isActive = currentPage === item.slug
                    const prefetch = prefetchHandlersBySlug[item.slug]
                    return (
                      <li key={item.slug} className="min-w-0">
                        <a
                          data-testid={item.testId}
                          href={item.route}
                          onClick={(e) => {
                            e.preventDefault()
                            handleLinkClick(item.slug)
                          }}
                          onMouseEnter={prefetch?.onMouseEnter}
                          onFocus={prefetch?.onFocus}
                          aria-label={t(item.ariaLabelKey)}
                          aria-current={isActive ? 'page' : undefined}
                          className={`navbar-link block max-w-full truncate px-3 py-2 ${isActive ? 'text-white border-b-2 border-crypto-accent font-medium' : 'text-crypto-muted hover:text-white font-medium'} transition-all`}
                        >
                          {t(item.labelKey)}
                        </a>
                      </li>
                    )
                  })}
                </>
              )}
            </ul>
          )}

          {/* Authentication/User Profile Section */}
          <div
            data-testid="navbar-actions"
            className="flex items-center justify-end space-x-2 sm:space-x-4 flex-shrink-0 grow basis-0"
          >
            {/* Deployed version indicator now lives in the global AppFooter
                (see @/shared/components/layout/AppFooter), not the navbar. */}

            {/* Language Selector */}
            <LanguageSelector
              variant="dropdown"
              size="sm"
              position="right"
              showFlags
              showLabels={false}
            />

            {/* Workspace switcher: renders only with more than one workspace */}
            {isAuthenticated && <WorkspaceSwitcher onOpenOverview={() => onNavigate('clientes')} />}

            {/* Notifications Panel */}
            {isAuthenticated && <NotificationsPanel />}

            {/* Authenticated User */}
            {isAuthenticated && user ? (
              <div className="relative" ref={menuRef}>
                <button
                  data-testid="user-menu-trigger"
                  data-user-button
                  onClick={toggleUserMenu}
                  // Below `md` the name is hidden and below `sm` the chevron too, so
                  // the visible content is only the initials: the label keeps the
                  // user's full name as the button's accessible name at every width.
                  aria-label={getUserName()}
                  aria-haspopup="true"
                  aria-expanded={userMenuOpen}
                  className="flex items-center space-x-3 bg-crypto-card p-2 rounded-lg hover:bg-crypto-border/30 transition-colors border border-crypto-border/50"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-white">{getUserInitials()}</span>
                  </div>
                  <span
                    className="hidden md:inline max-w-40 truncate text-sm font-medium text-crypto-text"
                    data-testid="user-display-name"
                  >
                    {getUserName()}
                  </span>
                  <svg
                    data-testid="user-menu-chevron"
                    aria-hidden="true"
                    className={`hidden sm:block w-4 h-4 text-crypto-muted transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* User Menu Dropdown */}
                {userMenuOpen && (
                  <div
                    data-testid="navbar-user-dropdown"
                    className="absolute right-0 mt-2 w-48 glass-effect rounded-xl p-2 transform transition-all duration-200 border border-crypto-border/50 bg-crypto-card"
                  >
                    <div className="space-y-1">
                      <button
                        data-testid="navbar-user-perfil"
                        onClick={() => {
                          onNavigate('perfil')
                          setUserMenuOpen(false)
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-crypto-muted hover:text-crypto-text hover:bg-crypto-accent/10 transition-all text-left"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <span className="text-sm">{t('user.profile')}</span>
                      </button>
                      <button
                        data-testid="nav-settings"
                        onClick={() => {
                          onNavigate('perfil')
                          setUserMenuOpen(false)
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-crypto-muted hover:text-crypto-text hover:bg-crypto-accent/10 transition-all text-left"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="text-sm">{t('user.settings')}</span>
                      </button>
                      <button
                        data-testid="navbar-user-theme-toggle"
                        onClick={() => {
                          toggleTheme()
                          setUserMenuOpen(false)
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-crypto-muted hover:text-crypto-text hover:bg-crypto-accent/10 transition-all text-left"
                      >
                        {/*
                          Every other row in this menu (Perfil, Configuración, Soporte, Cerrar
                          Sesión) names the ACTION it performs, and so does this one now:
                          «Cambiar a tema oscuro». It used to read «Tema: Oscuro» — the colon
                          turned an action into a state claim, so the row asserted "the theme
                          is dark" while its own sun icon said light.
                          Icon and text therefore both point at the DESTINATION: the moon is
                          shown while the light theme is active (clicking goes dark), the sun
                          while the dark theme is active.
                        */}
                        <svg
                          data-testid="navbar-theme-target-light"
                          className={`w-4 h-4 theme-icon-target-light ${resolvedTheme === 'dark' ? '' : 'hidden'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                        <svg
                          data-testid="navbar-theme-target-dark"
                          className={`w-4 h-4 theme-icon-target-dark ${resolvedTheme === 'light' ? '' : 'hidden'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                          />
                        </svg>
                        <span
                          className="text-sm theme-text"
                          data-testid="navbar-theme-toggle-label"
                        >
                          {resolvedTheme === 'dark'
                            ? t('user.switchToLightTheme')
                            : t('user.switchToDarkTheme')}
                        </span>
                      </button>
                      <button
                        data-testid="navbar-user-soporte"
                        onClick={() => {
                          onNavigate('soporte')
                          setUserMenuOpen(false)
                        }}
                        onMouseEnter={prefetchSupport.onMouseEnter}
                        onFocus={prefetchSupport.onFocus}
                        aria-label={t('navigation.ariaLabels.goToSupport')}
                        className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-crypto-muted hover:text-crypto-text hover:bg-crypto-accent/10 transition-all text-left"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                        <span className="text-sm">{t('navigation.support')}</span>
                      </button>
                      <div className="border-t border-crypto-border my-2" />
                      <button
                        data-testid="navbar-user-logout"
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-crypto-danger hover:text-crypto-danger/80 hover:bg-crypto-danger/10 transition-all text-left"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3v1"
                          />
                        </svg>
                        <span className="text-sm">{t('user.logout')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Unauthenticated State - Login/Register Buttons. Hidden below sm,
                 where they pushed «register» to x=281..370 on a 320 px phone; the
                 hamburger (shown below md) offers both actions there. */
              <div
                data-testid="navbar-auth-buttons"
                className="hidden sm:flex items-center space-x-2"
              >
                <button
                  data-testid="navbar-login-button"
                  onClick={() => onOpenAuthModal('login')}
                  className="px-4 py-2 text-sm font-medium text-crypto-muted hover:text-white transition-colors"
                >
                  {t('auth.signIn')}
                </button>
                <button
                  data-testid="navbar-register-button"
                  onClick={() => onOpenAuthModal('register')}
                  className="px-4 py-2 text-sm font-medium bg-crypto-accent hover:bg-crypto-accent-light text-white rounded-lg transition-colors"
                >
                  {signUpLabel}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu - Only show private pages when authenticated */}
        {mobileMenuOpen && (
          <div
            id="navbar-mobile-menu"
            data-testid="navbar-mobile-menu"
            // Must match the hamburger button breakpoint above (xl for authenticated nav)
            className={`${isAuthenticated ? 'xl:hidden' : 'md:hidden'} absolute top-full left-0 right-0 glass-effect border-b border-crypto-border`}
          >
            <div className="px-6 py-4 space-y-2">
              {isAuthenticated ? (
                user?.role === 'org_admin' ? (
                  // Admin mobile menu
                  <a
                    data-testid="navbar-mobile-link-admin-org"
                    href="/admin/org/dashboard"
                    onClick={(e) => {
                      e.preventDefault()
                      handleLinkClick('admin-org')
                      setMobileMenuOpen(false)
                    }}
                    className={`block px-3 py-2 rounded-md ${currentPage === 'admin-org' ? 'text-white bg-crypto-accent' : 'text-crypto-muted hover:text-white hover:bg-crypto-card/50'} transition-all`}
                  >
                    {t('navbar.organizationPanel')}
                  </a>
                ) : (
                  // User normal mobile menu — same list as the desktop nav, so
                  // the hamburger (the ONLY nav below xl) can never fall behind.
                  <>
                    {AUTHENTICATED_MAIN_NAV_ITEMS.map((item) => {
                      const isActive = currentPage === item.slug
                      const prefetch = prefetchHandlersBySlug[item.slug]
                      return (
                        <a
                          key={item.slug}
                          data-testid={item.mobileTestId}
                          href={item.route}
                          onClick={(e) => {
                            e.preventDefault()
                            handleLinkClick(item.slug)
                            setMobileMenuOpen(false)
                          }}
                          onMouseEnter={prefetch?.onMouseEnter}
                          onFocus={prefetch?.onFocus}
                          aria-label={t(item.ariaLabelKey)}
                          aria-current={isActive ? 'page' : undefined}
                          className={`block px-3 py-2 rounded-md ${isActive ? 'text-white bg-crypto-accent' : 'text-crypto-muted hover:text-white hover:bg-crypto-card/50'} transition-all`}
                        >
                          {t(item.labelKey)}
                        </a>
                      )
                    })}
                  </>
                )
              ) : (
                // Unauthenticated mobile menu - show login/register options
                <>
                  <button
                    data-testid="navbar-mobile-login"
                    onClick={() => {
                      onOpenAuthModal('login')
                      setMobileMenuOpen(false)
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-crypto-muted hover:text-white hover:bg-crypto-card/50 transition-all"
                  >
                    {t('auth.signIn')}
                  </button>
                  <button
                    data-testid="navbar-mobile-register"
                    onClick={() => {
                      onOpenAuthModal('register')
                      setMobileMenuOpen(false)
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md bg-crypto-accent hover:bg-crypto-accent-light text-white transition-all"
                  >
                    {signUpLabel}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    )
  },
)

Navbar.displayName = 'Navbar'
